import { useEffect, useState } from 'react';
import {
  CategoryPillRow,
  type HeaderCategory,
} from '../../../modules/design-system';
import { BalancePill } from '../../../modules/wallet';
import { useSession } from '../../../modules/session';
import { useAuth } from '../../../modules/auth/useAuth';
import { useVoluntaryTopUp } from '../../../modules/payments/useVoluntaryTopUp';
import { goToLobby, useOperatorLinks } from '../../../modules/operatorLinks';
import { CategoryDropdownPill } from './CategoryDropdownPill';
import { MyPacksPill } from './MyPacksPill';
import { LoginPill } from './LoginPill';
import { LobbyPill } from './LobbyPill';

export type { HeaderCategory };

export interface HeaderProps {
  categories: HeaderCategory[];
  activeCategoryId?: string;
  onCategorySelect?: (id: string) => void;
  onMyPacksClick?: () => void;
  className?: string;
}

function useIsScrolled(threshold = 8): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > threshold);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [threshold]);
  return scrolled;
}

/**
 * Modern header — Figma `543:6352` (desktop) / `581:564` (mobile).
 *
 *   < 600px → single CategoryDropdownPill (active category + chevron) on
 *             the left, glass MyPacksPill on the right.
 *   ≥ 600px → horizontal CategoryPillRow flush-left, same MyPacksPill on
 *             the right.
 *
 * With a casino `lobby_url` the LobbyPill precedes the category control in
 * the left slot on both breakpoints.
 *
 * No logo on either breakpoint. 10px vertical padding on mobile, 20px from
 * 600px up. On scroll the bar
 * darkens for legibility — same affordance as the classic header.
 */
export function Header({
  categories,
  activeCategoryId,
  onCategorySelect,
  onMyPacksClick,
  className = '',
}: HeaderProps) {
  const scrolled = useIsScrolled(8);
  const session = useSession();
  const { requestAuth } = useAuth();
  const loggedIn = session.getToken() != null;
  // Stripe operators → interactive pill (opens the deposit flow); seamless →
  // undefined → read-only balance display (staging behavior).
  const onBalanceTap = useVoluntaryTopUp();
  // Casino `lobby_url` from the launch payload → "Back to lobby" pill.
  // Null (operator didn't send one) → no pill, header unchanged.
  const { lobbyUrl } = useOperatorLinks();

  return (
    <header
      data-scrolled={scrolled || undefined}
      data-co-component="Header"
      className={
        // `header-safe-top` + `top-0`, not `top-[var(--page-safe-top)]`: the
        // glass has to reach the physical top edge (iOS nav-bar look), so the
        // bar bleeds into the host safe area and pads its controls back down
        // (index.css). The bar's own vertical padding therefore lives on the
        // content container below, where it can't fight that padding-top.
        '@container sticky top-0 z-40 header-safe-top ' +
        'transition-[border-color] duration-200 ease-out ' +
        (scrolled ? 'border-b border-white/10 ' : 'border-b border-transparent ') +
        className
      }
    >
      {/* Scroll-darken glass layer. Not on <header> itself: a backdrop-filter
          there would make the bar the backdrop root of the category dropdown,
          whose own glass would then blur nothing below the bar and go clear. */}
      <span
        aria-hidden
        className={
          'pointer-events-none absolute inset-0 -z-[1] ' +
          'transition-[background-color,backdrop-filter] duration-200 ease-out ' +
          (scrolled ? 'bg-black/40 backdrop-blur-md' : '')
        }
      />

      {/* Operator-theme tint layer — spans full header width so the
          scroll-darken effect (and any --co-Header-bg override) reaches
          the viewport edges, NOT just the content container. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-[1]"
        style={{ background: 'var(--co-Header-bg)', color: 'var(--co-Header-text)' }}
      />

      {/* Content container — mirrors PackLayout's inner wrapper so the
          header's category pills + MyPacks pin align with the slider's
          left edge and the sidebar's right edge across every viewport. */}
      <div
        className="
          mx-auto max-w-350
          flex items-center justify-between gap-3
          px-4 py-2.5 @[600px]:px-6 @[600px]:py-5 @[900px]:px-8
        "
      >
        {/* Left slot — the lobby pill leads, so its back-chevron sits on the
            screen edge the user reads "out of the game" from, ahead of the
            category control on every width. */}
        <div className="flex min-w-0 flex-1 items-center gap-2 @[600px]:gap-3">
          {lobbyUrl && <LobbyPill onClick={() => goToLobby(lobbyUrl)} />}

          {/* Mobile — single dropdown pill with listbox popover */}
          {categories.length > 0 && (
            <div className="@[600px]:hidden">
              <CategoryDropdownPill
                categories={categories}
                {...(activeCategoryId !== undefined ? { activeId: activeCategoryId } : {})}
                {...(onCategorySelect ? { onSelect: onCategorySelect } : {})}
              />
            </div>
          )}

          {/* Desktop — full pill row */}
          <div className="hidden min-w-0 flex-1 @[600px]:block">
            <CategoryPillRow
              categories={categories}
              {...(activeCategoryId !== undefined ? { activeId: activeCategoryId } : {})}
              {...(onCategorySelect ? { onSelect: onCategorySelect } : {})}
              centered={false}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 @[600px]:gap-3">
          {loggedIn ? (
            <>
              <BalancePill surface="header" {...(onBalanceTap ? { onClick: onBalanceTap } : {})} />
              <MyPacksPill {...(onMyPacksClick ? { onClick: onMyPacksClick } : {})} />
            </>
          ) : (
            <LoginPill onClick={() => void requestAuth()} />
          )}
        </div>
      </div>
    </header>
  );
}
